import {
  AppState,
  userDeselectedAnnotation,
  userClickedAnnotation,
  Action,
  Geometry,
  Annotation,
  CoordinateSystems,
} from './app.reducer';
import { guid } from '../utils/utils';
import { MapClick } from '../labeling-screen/segment-image';
import {
  ClassificationField,
  Tool,
} from '../customization';

export const getSelectedRectangleTool = (state: AppState) => {
  return state.tools.find((tool) => tool.tool === 'rectangle' && tool.id === state.currentToolId);
}

export const selectIntentFromMapClick = (state: AppState, click: MapClick): Action | undefined => {
  if (Array.isArray(state.drawnAnnotationBounds) && state.drawnAnnotationBounds.length > 0) {
    return undefined;
  }

  if (!state.currentToolId && !click.shapeId){
    return userDeselectedAnnotation();
  } else if (click.shapeId){
    return userClickedAnnotation(click.shapeId);
  }

  return undefined;
}


function parseIfPossible(str: string | undefined){
  if (!str){
    return undefined;
  }
  try {
    return JSON.parse(str);
  } catch(e) {
    return undefined;
  }
}


const selectToolByName = (state: AppState, toolName: string) => {
  return state.tools.find((tool) => tool.name === toolName);
}

// Since labels can be generated for both maps and images
// we support both XY and LatLng for labels
interface Point {
  x?: number,
  y?: number,
  lat?: number,
  lng?: number
}

export const selectAnnotationsFromLabel = (state: AppState, label: string | undefined): Annotation[] => {
  const classes = parseIfPossible(label);
  if (!classes){
    return [];
  }

  return Object.keys(classes).reduce((annotations, className) => {
    const tool = selectToolByName(state, className);
    if (!tool){
      return annotations;
    }
    const newAnnotations = classes[className].map((shape: {geometry: Point[] | Point, [classificationName: string]: any}) => {
      let geometry: Point[] | Point;
      let classifications:{[classifcationName: string]: any};
      // Shape should never be an array but image segmentation V2
      // saved buildings: [[{x: 3, y: 3]]
      // then image segmentation V3 saves as
      // saved buildings: [{geometry: [{x: 3, y: 3]}]
      // so this is a backward comptiable way of doing this
      if (Array.isArray(shape)) {
        geometry = shape;
        classifications = {};
      } else {
        let { geometry: currentGeometry, ...otherClassifications } = shape;
        geometry = currentGeometry;
        classifications = otherClassifications
      }

      const toCoord = ({y, x, lat, lng}:Point) => ({lat: y !== undefined ? y : lat, lng: x !== undefined ? x : lng});
      const formattedGeometry = Array.isArray(geometry) ? geometry.map(toCoord) : toCoord(geometry);

      const classificationAnswers = Object.keys(classifications || {}).map((classificationName) => {
        const field = (tool.classifications || []).find(({name}) => name === classificationName)
        if (field) {
          return {
            [field.id]: classifications[classificationName]
          }
        } else {
          console.log(`Couldn't find classification "${classificationName}" in tool ${tool.classifications}`)
          return {}
        }
      })

      return {
        id: guid(),
        geometry: formattedGeometry,
        color: tool.color,
        editing: false,
        toolName: tool.tool,
        toolId: tool.id,
        classificationAnswers: Object.assign({}, ...classificationAnswers),
      }
    });

    return [...annotations, ...newAnnotations];
  }, []);
}


export const selectClassificationFieldsFromLabel = (state: AppState, label: string | undefined): ClassificationField[] => {
  const classificationWithNoAnswers = state.classificationFields.map(({userAnswer, ...field}) => field);
  const info = parseIfPossible(label);
  if (!info){
    return classificationWithNoAnswers;
  }

  return classificationWithNoAnswers.map((field) => {
    return {
      ...field,
      userAnswer: info[field.name]
    };
  });
}

// If the user is labeling aerial imagegry on a map
// then we want to keep the label in {lat: number, lng: number}
// however if they are lableing an image we want {x: number, y: number}
function shouldConvertAnnotationsToXy(state: AppState): Boolean | undefined{
  const epsg = state.tileLayerInfo && state.tileLayerInfo.epsg;
  if (!epsg) {
    return undefined;
  }
  return epsg === CoordinateSystems.Simple;
}

function convertAnnotationToXY(geometry: Geometry) {
  const toPoint = ({lat, lng}: {lat: number, lng: number}) => ({
    // These leaflet Latlngs have like 13 decimal points
    // pixels locations dont have decimal points
    x: Math.round(lng),
    y: Math.round(lat)
  });
  return Array.isArray(geometry) ? geometry.map(toPoint) : toPoint(geometry);
};

// geometry is a leaflet lat lng
function seralizeGeometry(geometry: Geometry) {
  return Array.isArray(geometry) ?
    geometry.map(({lat, lng}) => ({lat, lng})) :
    {lat: geometry.lat, lng: geometry.lng};
}


export const selectLabelFromState = (state: AppState) => {
  const asXy = shouldConvertAnnotationsToXy(state);
  if (asXy === undefined) {
    return JSON.stringify({});
  }

  const annotationsByTool = state.annotations.reduce((annotationsByToolTemp, annotation) => {
    if (!annotationsByToolTemp[annotation.toolId]) {
      annotationsByToolTemp[annotation.toolId] = []
    }

    return {
      ...annotationsByToolTemp,
      [annotation.toolId]: [
        ...annotationsByToolTemp[annotation.toolId],
        annotation
      ]
    };
  }, {});

  const labelWithAnnotations = Object.keys(annotationsByTool).reduce((labelTemp, toolId) => {
    const tool = state.tools.find(({id}) => id === toolId);
    if (!tool) {
      throw new Error('Tool not found: ' + toolId);
    }

    const getNameFromNestedClassificationId = (classificationId: string) => {
      const foundClassification = state.tools.map((foundTool) => {
        return (foundTool.classifications || []).find(({id}) => id === classificationId);
      }).filter(Boolean)[0]

      if (!foundClassification){
        throw new Error('Unable to find nested classifcation tool name for '+classificationId);
      }

      return foundClassification.name
    }

    const convertGeometry = asXy ? convertAnnotationToXY : seralizeGeometry;
    const transformToLabel = (annotation: Annotation) => {
      const classifications = Object.keys(annotation.classificationAnswers || {})
        .map((nestedClassificationId) => ({
          [getNameFromNestedClassificationId(nestedClassificationId)]: (annotation.classificationAnswers || {})[nestedClassificationId]
        }))

      return {
        ... Object.assign({}, ...classifications),
        geometry: convertGeometry(annotation.geometry)
      }
    }

    return {
      ...labelTemp,
      [tool.name]: annotationsByTool[toolId].map(transformToLabel)
    }

  }, {})

  const labelWithClassifications = state.classificationFields.reduce((classifications, field) => {
    if (field.userAnswer) {
      return {
        ...classifications,
        [field.name]: field.userAnswer
      };
    } else {
      return classifications;
    }
  }, {});

  return JSON.stringify(
    Object.assign({}, labelWithAnnotations, labelWithClassifications)
  );
}


export function selectDoesStateIncludeUnsavedChanges(state: AppState){
  if (state.label) {
    // We dont set this.state.label until the user clicks confirm
    // TODO selectDoesStateHaveUnsavedChanges()
    const labelDerviedFromState = selectLabelFromState(state);
    if (state.label === 'Skip' && labelDerviedFromState === '{}'){
      return false;
    }
    if (state.label !== labelDerviedFromState) {
      return true;
    }

    return false;
  } else {
    // TODO I dont like that state.label isn't saved until we sumbit
    if (state.annotations.length > 0) {
      return true;
    }

    const userAnsweredClassification = state.classificationFields.some(({userAnswer}) => Boolean(userAnswer));
    if (userAnsweredClassification){
      return true;
    }

    return false;
  }

}

export function isUserDrawingRectangle(state: AppState): Boolean{
  return Boolean(state.rectangleInProgressId);
}

interface WMSTileLayer {
  tileLayerUrl: string,
  minZoom?: number,
  maxZoom?: number,
  bounds?: [number, number][]
}

export function selectTileLayerWMSInformation(data: string): WMSTileLayer | undefined{
  try{
    const decoded = JSON.parse(data)

    if (!decoded || !decoded.WMSTileLayer){
      return undefined
    }

    return {
      tileLayerUrl: decoded.WMSTileLayer,
      bounds: decoded.bounds,
      minZoom: decoded.minZoom,
      maxZoom: decoded.maxZoom,
    };
  } catch(e){
    return undefined;
  }
}

export function selectCurrentTools(tools: Tool[], searchValue: string): Tool[]{
  return tools
    .filter(({name}) => name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1)
}

export function getSelectedAnnotation(state: AppState){
  return state.annotations.find(({editing}) => editing);
}

function getToolForSelectedAnnotation(state: AppState): Tool | undefined{
  const selectedAnnotation = getSelectedAnnotation(state);
  if (!selectedAnnotation){
    return undefined;
  }

  return state.tools.find(({id}) => id === selectedAnnotation.toolId);
}

function getNestedClassificationFieldsForSelectedAnnotation(state: AppState): ClassificationField[] | undefined{
  const tool = getToolForSelectedAnnotation(state);
  if (!tool){
    return undefined;
  }

  if (!tool.classifications){
    return undefined;
  }

  const selectedAnnotation = getSelectedAnnotation(state);
  const nestedClassificationAnswers = selectedAnnotation && selectedAnnotation.classificationAnswers || {};
  return tool.classifications.map((classification) => {
    return {
      ...classification,
      userAnswer: nestedClassificationAnswers[classification.id]
    };
  });
}

export function selectToolbarState(state: AppState) {
  const {tools, annotations, hiddenTools, searchValue} = state;
  // When an annotation is selected we zoom into it
  if (getNestedClassificationFieldsForSelectedAnnotation(state)){
    return [];
  }

  return selectCurrentTools(tools, searchValue)
    .map(({id, name, color, tool}) => {
      return {
        id,
        name,
        color,
        tool,
        count: annotations.filter(({toolId}) => toolId === id).length,
        visible: hiddenTools.indexOf(id) === -1
      };
    });
}

export function selectShownClassifications(state: AppState, searchValue: string): ClassificationField[]{
  const fields = getNestedClassificationFieldsForSelectedAnnotation(state);
  if (fields){
    return fields;
  }

  const classifications = state.classificationFields;
  const isSubstring = (strOne: string, strTwo: string) => strOne.toLowerCase().indexOf(strTwo.toLowerCase()) > -1
  const matchesInstructions = ({instructions}: ClassificationField) => isSubstring(instructions, searchValue);
  const matchesOptions = ({options}: ClassificationField) => options.some(({label}) => isSubstring(label, searchValue))
  return classifications.reduce((remainingClassifications, classification) => {
    if (matchesInstructions(classification)){
      return [...remainingClassifications, classification]
    } else if (matchesOptions(classification)){
      const filterToMatchingOptions = (classificationField: ClassificationField): ClassificationField => {
        return {
          ...classificationField,
          options: classificationField.options.filter(({label}) => isSubstring(label, searchValue))
        }
      }
      return [...remainingClassifications, filterToMatchingOptions(classification)]
    } else {
      return remainingClassifications
    }
  }, [])
}

export function selectNestedHeaderTitle(state: AppState): string | undefined {
getNestedClassificationFieldsForSelectedAnnotation(state);
  const tool = getToolForSelectedAnnotation(state);

  if (tool && tool.classifications) {
    return `Classify this ${tool.name.toLowerCase()}`;
  } else {
    return undefined;
  }
}



// WEBPACK FOOTER //
// ./src/redux/app.selectors.tsx